import apiClient from '../utils/axios-config';
import { Outfit, OutfitMetadata } from '../types';
import { logger } from '../utils/logger';
import ActionTracker from './action-tracker';

class OutfitService {
  /**
   * Get trending outfits
   */
  async getTrending(limit: number = 20): Promise<Outfit[]> {
    try {
      // Use the working endpoint with trending sort instead of broken /outfits/trending
      const response = await apiClient.get(`/outfits?sortBy=trending&limit=${limit}`);
      
      // Handle backend response format: { success: true, outfits: [...] }
      const outfits = response.data.outfits || [];
      let processedOutfits = Array.isArray(outfits) ? outfits : [];
      
      // Transform data to match frontend expectations
      processedOutfits = processedOutfits
        .filter((outfit: any) => {
          const id = outfit.outfitId || outfit.id;
          return id && id.length > 10 && !id.startsWith('outfit-');
        })
        .map((outfit: any) => ({
          ...outfit,
          id: outfit.outfitId || outfit.id, // Map outfitId to id
          title: outfit.title || outfit.description, // Use description as title if title is missing
          createdAt: outfit.createdAt, // Ensure createdAt is preserved
        }));
      
      // Debug logging for date issues
      if (processedOutfits.length > 0) {
        console.log('Trending outfits sample:', {
          count: processedOutfits.length,
          firstOutfit: {
            id: processedOutfits[0].id,
            outfitId: processedOutfits[0].outfitId,
            createdAt: processedOutfits[0].createdAt,
            updatedAt: processedOutfits[0].updatedAt,
            title: processedOutfits[0].title,
            description: processedOutfits[0].description
          }
        });
      }
      
      return processedOutfits;
    } catch (error) {
      logger.apiError('fetching trending outfits', error);
      throw error;
    }
  }

  /**
   * AI-powered search using OpenSearch + Bedrock embeddings
   * Used by the top search bar for semantic/vector search
   */
  async aiSearchOutfits(params: {
    query: string;
    limit?: number;
  }): Promise<{ outfits: Outfit[]; pagination: any }> {
    try {
      const searchParams = new URLSearchParams();
      
      if (!params.query || !params.query.trim()) {
        return {
          outfits: [],
          pagination: { total: 0, page: 1, totalPages: 1 }
        };
      }
      
      searchParams.append('q', params.query);
      // Fetch more results from backend to allow client-side filtering
      searchParams.append('limit', (params.limit || 50).toString());

      const url = `/search/ai?${searchParams.toString()}`;
      console.log('🤖 AI Search API call:', url);
      
      const response = await apiClient.get(url);
      
      // Transform the data to match frontend expectations
      const data = response.data;
      if (data.outfits && Array.isArray(data.outfits)) {
        data.outfits = data.outfits
          .filter((outfit: any) => {
            const id = outfit.outfitId || outfit.id;
            return id && id.length > 10 && !id.startsWith('outfit-');
          })
          .map((outfit: any) => ({
            ...outfit,
            id: outfit.outfitId || outfit.id,
            title: outfit.title || outfit.description || 'Untitled Outfit'
          }));
      }
      
      return {
        outfits: data.outfits || [],
        pagination: { 
          total: data.count || 0, 
          page: 1, 
          totalPages: 1 
        }
      };
    } catch (error: any) {
      console.error('AI Search error:', error);
      logger.apiError('AI searching outfits', error);
      
      // Return empty results on error
      return {
        outfits: [],
        pagination: { total: 0, page: 1, totalPages: 1 }
      };
    }
  }

  /**
   * Basic search using DynamoDB keyword search
   * Used by the Search page search box for simple text matching
   */
  async searchOutfits(params: {
    query?: string;
    eventTypes?: string[];
    brands?: string[];
    clothingTypes?: string[];
    genderCategories?: string[];
    sortBy?: string;
    page?: number;
    limit?: number;
  }): Promise<{ outfits: Outfit[]; pagination: any }> {
    try {
      const searchParams = new URLSearchParams();
      
      // Basic search endpoint - uses DynamoDB scan
      if (params.query) searchParams.append('q', params.query);
      if (params.eventTypes?.length) {
        params.eventTypes.forEach((type) => searchParams.append('eventType', type));
      }
      if (params.brands?.length) {
        params.brands.forEach((brand) => searchParams.append('brand', brand));
      }
      if (params.clothingTypes?.length) {
        params.clothingTypes.forEach((type) => searchParams.append('clothingType', type));
      }
      if (params.genderCategories?.length) {
        params.genderCategories.forEach((g) => searchParams.append('gender', g));
      }
      if (params.sortBy) {
        const sortByValue = typeof params.sortBy === 'string' ? params.sortBy.toLowerCase() : params.sortBy;
        searchParams.append('sortBy', sortByValue);
      }
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());

      const url = `/search?${searchParams.toString()}`;
      console.log('🔍 Basic Search API call:', url);
      
      const response = await apiClient.get(url);
      
      // Transform the data to match frontend expectations
      const data = response.data;
      if (data.outfits && Array.isArray(data.outfits)) {
        data.outfits = data.outfits
          .filter((outfit: any) => {
            const id = outfit.outfitId || outfit.id;
            return id && id.length > 10 && !id.startsWith('outfit-');
          })
          .map((outfit: any) => ({
            ...outfit,
            id: outfit.outfitId || outfit.id,
            title: outfit.title || outfit.description || 'Untitled Outfit'
          }));
      }
      
      return {
        outfits: data.outfits || [],
        pagination: { 
          total: data.count || 0, 
          page: 1, 
          totalPages: 1 
        }
      };
    } catch (error: any) {
      console.error('Search error:', error);
      logger.apiError('searching outfits', error);
      
      // Return empty results on error
      return {
        outfits: [],
        pagination: { total: 0, page: 1, totalPages: 1 }
      };
    }
  }

  /**
   * Browse outfits without search query - for discovery and filtering
   */
  async browseOutfits(params: {
    eventTypes?: string[];
    brands?: string[];
    clothingTypes?: string[];
    genderCategories?: string[];
    sortBy?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{ outfits: Outfit[]; pagination: any }> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params.eventTypes?.length) {
        params.eventTypes.forEach((type) => searchParams.append('eventType', type));
      }
      if (params.brands?.length) {
        params.brands.forEach((brand) => searchParams.append('brand', brand));
      }
      if (params.clothingTypes?.length) {
        params.clothingTypes.forEach((type) => searchParams.append('clothingType', type));
      }
      if (params.genderCategories?.length) {
        params.genderCategories.forEach((g) => searchParams.append('gender', g));
      }
      if (params.sortBy) {
        // Ensure sortBy is lowercase for API compatibility
        const sortByValue = typeof params.sortBy === 'string' ? params.sortBy.toLowerCase() : params.sortBy;
        searchParams.append('sortBy', sortByValue);
      }
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());

      const url = `/outfits?${searchParams.toString()}`;
      const response = await apiClient.get(url);
      
      // Handle the response format from /outfits endpoint: { outfits: [...], pagination: {...} }
      const data = response.data;
      const outfits = data.outfits || [];
      
      // Filter and transform outfits
      const filteredOutfits = Array.isArray(outfits) ? outfits
        .filter((outfit: any) => {
          const id = outfit.outfitId || outfit.id;
          return id && id.length > 10 && !id.startsWith('outfit-');
        })
        .map((outfit: any) => ({
          ...outfit,
          id: outfit.outfitId || outfit.id,
          title: outfit.title || outfit.description || 'Untitled Outfit'
        })) : [];
      
      return {
        outfits: filteredOutfits,
        pagination: data.pagination || { 
          total: filteredOutfits.length, 
          page: 1, 
          totalPages: 1,
          hasMore: data.pagination?.hasMore || false
        }
      };
    } catch (error: any) {
      console.error('Browse error:', error);
      logger.apiError('browsing outfits', error);
      
      // Return empty results on error
      return {
        outfits: [],
        pagination: { total: 0, page: 1, totalPages: 1 }
      };
    }
  }

  /**
   * Get outfits by event type - using main outfits endpoint for consistent user enrichment
   */
  async getOutfitsByEventType(eventType: string, limit: number = 10): Promise<Outfit[]> {
    try {
      // Use the main /outfits endpoint with event parameter for consistent user enrichment
      const response = await apiClient.get(`/outfits?event=${encodeURIComponent(eventType)}&limit=${limit}&sortBy=newest`);
      
      // Handle standard outfits endpoint response format: { outfits: [...], pagination: {...} }
      const outfits = response.data.outfits || [];
      
      console.log(`getOutfitsByEventType(${eventType}): Found ${outfits.length} outfits with user enrichment`);
      
      // Filter and transform data to match frontend expectations
      return outfits
        .filter((outfit: any) => {
          const id = outfit.outfitId || outfit.id;
          return id && id.length > 10 && !id.startsWith('outfit-');
        })
        .map((outfit: any) => ({
          ...outfit,
          id: outfit.outfitId || outfit.id, // Map outfitId to id
          title: outfit.title || outfit.description, // Use description as title if title is missing
        }));
    } catch (error) {
      console.error(`Error fetching outfits for event type ${eventType}:`, error);
      logger.apiError(`fetching outfits for event type ${eventType}`, error);
      throw error;
    }
  }

  /**
   * Get outfit by ID
   */
  async getOutfitById(id: string): Promise<Outfit> {
    try {
      const response = await apiClient.get(`/outfits/${id}`);
      const outfit = response.data;
      
      // Debug logging for individual outfit
      console.log('Fetched outfit:', {
        id: outfit.id,
        createdAt: outfit.createdAt,
        updatedAt: outfit.updatedAt,
        title: outfit.title || outfit.description
      });
      
      return outfit;
    } catch (error: any) {
      // For 404 errors, the outfit was likely deleted
      if (error.response?.status === 404) {
        console.log(`Outfit ${id} not found (likely deleted)`);
        const deletedError = new Error('This outfit has been removed or is no longer available.');
        (deletedError as any).isDeleted = true;
        throw deletedError;
      }
      logger.apiError('fetching outfit', error);
      throw error;
    }
  }

  /**
   * Upload outfit using the proper flow: presigned URL -> S3 -> create record
   */
  async uploadOutfit(file: File, metadata: OutfitMetadata): Promise<Outfit> {
    try {
      // Import the upload service dynamically to avoid circular dependencies
      const UploadService = (await import('./upload-service')).default;
      const outfit = await UploadService.uploadOutfit(file, metadata);
      
      // Track action for discovery algorithm
      ActionTracker.trackOutfitUpload(outfit.id);
      
      return outfit;
    } catch (error) {
      logger.apiError('uploading outfit', error);
      throw error;
    }
  }

  /**
   * Get outfits uploaded by a specific user
   */
  async getUserOutfits(userId?: string, params?: {
    limit?: number;
    lastKey?: string;
    sortBy?: 'createdAt' | 'likes' | 'rating';
  }): Promise<{ outfits: Outfit[]; pagination: any; userId: string }> {
    try {
      const searchParams = new URLSearchParams();
      
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.lastKey) searchParams.append('lastKey', params.lastKey);
      if (params?.sortBy) {
        // Ensure sortBy is lowercase for API compatibility
        const sortByValue = typeof params.sortBy === 'string' ? params.sortBy.toLowerCase() : params.sortBy;
        searchParams.append('sortBy', sortByValue);
      }

      const endpoint = userId 
        ? `/users/${userId}/outfits${searchParams.toString() ? '?' + searchParams.toString() : ''}`
        : `/users/me/outfits${searchParams.toString() ? '?' + searchParams.toString() : ''}`;

      const response = await apiClient.get(endpoint);
      return response.data;
    } catch (error) {
      logger.apiError('fetching user outfits', error);
      throw error;
    }
  }

  /**
   * Get current user's outfits (authenticated)
   */
  async getMyOutfits(params?: {
    limit?: number;
    lastKey?: string;
    sortBy?: 'createdAt' | 'likes' | 'rating';
  }): Promise<{ outfits: Outfit[]; pagination: any; userId: string }> {
    return this.getUserOutfits(undefined, params);
  }

  /**
   * Get liked outfits for a specific user
   */
  async getLikedOutfits(userId?: string, params?: {
    limit?: number;
    sortBy?: 'likedAt' | 'newest' | 'oldest' | 'rating';
  }): Promise<{ outfits: Outfit[]; pagination: any; userId: string }> {
    try {
      // Build query parameters
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.sortBy) searchParams.append('sortBy', params.sortBy);

      // Use the provided userId or 'me' for current user
      const userIdParam = userId || 'me';
      const endpoint = `/users/${userIdParam}/liked${searchParams.toString() ? '?' + searchParams.toString() : ''}`;

      const response = await apiClient.get(endpoint);
      
      // Handle different response formats from the API
      const responseData = response.data;
      
      // If the response has a nested data structure, extract it
      if (responseData && responseData.data && responseData.data.outfits) {
        return responseData.data;
      }
      
      // Otherwise return the response as-is (direct format)
      return responseData;
    } catch (error) {
      logger.apiError('fetching liked outfits', error);
      throw error;
    }
  }

  /**
   * Get current user's liked outfits (authenticated)
   */
  async getMyLikedOutfits(params?: {
    limit?: number;
    sortBy?: 'likedAt' | 'newest' | 'oldest' | 'rating';
  }): Promise<{ outfits: Outfit[]; pagination: any; userId: string }> {
    return this.getLikedOutfits(undefined, params);
  }
}

export default new OutfitService();